import axios from 'axios';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  State as ConfigurationState,
  RegionAndLocaleSet,
} from 'contexts/ConfigurationForm/types';

const enableProductApi = async (merchantId: string) => {
  const { data } = await axios.post(
    `/api/merchants/${merchantId}/enable-product-api`,
  );
  return data;
};

const disableProductApi = async (merchantId: string) => {
  const { data } = await axios.post(
    `/api/merchants/${merchantId}/disable-product-api`,
  );
  return data;
};

const patchMerchant = async (
  merchantId: string,
  merchant: { preorderPeriod: string | number },
) => {
  const { data } = await axios.patch(`/api/merchants/${merchantId}`, merchant);
  return data;
};

const createRegionAndLocaleSet = async (
  merchantId: string,
  regionAndLocaleSet: RegionAndLocaleSet,
) => {
  const { region, locale, enabled } = regionAndLocaleSet;
  const { data } = await axios.post(
    `/api/merchants/${merchantId}/region-locale-sets`,
    {
      region,
      locale,
      enabled,
    },
  );
  return data;
};

const patchRegionAndLocaleSet = async (
  merchantId: string,
  regionAndLocaleSet: RegionAndLocaleSet,
) => {
  const { id, enabled } = regionAndLocaleSet;
  const { data } = await axios.patch(
    `/api/merchants/${merchantId}/region-locale-sets/${id}`,
    {
      enabled,
    },
  );
  return data;
};

const mutateConfiguration = async (merchantId, state: ConfigurationState) => {
  const { editingState, currentState } = state;
  // 1. enable/disable product api
  if (editingState.productApiEnabled !== currentState.productApiEnabled) {
    if (editingState.productApiEnabled) {
      await enableProductApi(merchantId);
    } else {
      return await disableProductApi(merchantId); // after disabled, stop here
    }
  }

  // 2. update preorder period
  if (editingState.preorderPeriod !== currentState.preorderPeriod) {
    await patchMerchant(merchantId, {
      preorderPeriod: Number(editingState.preorderPeriod),
    });
  }

  // 3. update region locale sets
  const currentRegionAndLocaleSets = currentState.regionAndLocaleSets || [];
  for (let i = 0; i < editingState.regionAndLocaleSets.length; i++) {
    const regionAndLocaleSet = editingState.regionAndLocaleSets[i];
    const currentRegionAndLocaleSet = currentRegionAndLocaleSets[i];
    if (!currentRegionAndLocaleSet) {
      await createRegionAndLocaleSet(merchantId, regionAndLocaleSet);
      continue;
    }
    if (regionAndLocaleSet.enabled !== currentRegionAndLocaleSet.enabled) {
      await patchRegionAndLocaleSet(merchantId, regionAndLocaleSet);
      continue;
    }
  }

  return Promise.resolve();
};

export const useMutateConfiguration = ({ onSuccess, onError }) => {
  const { merchantId } = useParams();
  return useMutation(
    (state: ConfigurationState) => mutateConfiguration(merchantId, state),
    {
      onSuccess: (...args) => {
        if (typeof onSuccess === 'function') {
          onSuccess(...args);
        }
      },
      onError,
    },
  );
};
